import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";

const Passport = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [file, setFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);
  const [createdDate, setCreatedDate] = useState(null);
  const [isSigned, setIsSigned] = useState(false);

  const [expiryDate, setExpiryDate] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [issueCountry, setIssueCountry] = useState("");
  const [nationality, setNationality] = useState("");

  const [countries, setCountries] = useState([]);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // ✅ Wrapped in useCallback so we can safely use it in useEffect
  const loadDropdowns = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/dropdowns/countries`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setCountries(res.data);
    } catch (err) {
      console.error("Dropdown load error:", err);
    }
  }, [API_BASE]);

  const fetchPassport = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/passport/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const data = res.data;

      // 👉 Always set these fields (even if no PDF file exists)
      setExpiryDate(
        data.p_expiry_date
          ? new Date(data.p_expiry_date).toISOString().split("T")[0]
          : ""
      );

      setDocumentNumber(data.p_document_number || "");
      setIssueCountry(data.p_issue_country || "");
      setNationality(data.p_nationality || "");

      // 👉 Handle file fields separately
      if (data.p_file) {
        setUploadedFileName(data.p_file);
        setOriginalFileName(data.p_original);
        setCreatedDate(data.p_created_date);
      } else {
        setUploadedFileName(null);
        setOriginalFileName(null);
        setCreatedDate(null);
      }
    } catch (err) {
      console.error("Passport fetch error:", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    loadDropdowns();
    fetchPassport();
  }, [fetchPassport, loadDropdowns]);

  // Task signature tracking
  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const TASK_ID = 38;
        const idKey = String(TASK_ID);
        const status = res.data.status?.[idKey] ?? "not_completed";
        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("Signature status error:", err);
      }
    };
    if (userId) fetchTaskStatus();
  }, [userId, API_BASE]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected?.type === "application/pdf") setFile(selected);
    else alert("Only PDF files are allowed!");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("expiry_date", expiryDate);
    formData.append("document_number", documentNumber);
    formData.append("issue_country", issueCountry);
    formData.append("nationality", nationality);

    if (file) formData.append("file", file);

    try {
      const res = await axios.post(
        `${API_BASE}/api/passport/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      alert(res.data.message || "Saved successfully!");
      fetchPassport();
    } catch (err) {
      console.error("Upload failed", err);
      alert(err.response?.data?.message || "Save failed");
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>Passport</span>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-4 text-start">
            {/* Left side can stay empty or hold something else */}
          </div>
          <div className="col-4 text-center">
            <h3 className="mb-0">Passport</h3>
          </div>

          <div className="col-4 text-end">
            <div>New‑Way Trucking Ltd</div>
            <div>7725 48 St. SE, Calgary</div>
            <div>Alberta, Canada T2C 2V3</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-3">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label>Expiry Date *</label>
              <input
                type="date"
                className="form-control"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                disabled={!isAdmin && isSigned}
                required
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Document Number *</label>
              <input
                type="text"
                className="form-control"
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                disabled={!isAdmin && isSigned}
                required
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Issue Country *</label>
              <select
                className="form-control"
                value={issueCountry}
                onChange={(e) => setIssueCountry(e.target.value)}
                disabled={!isAdmin && isSigned}
                required
              >
                <option value="">Select country</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.country_name}>
                    {c.country_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 mb-3">
              <label>Nationality</label>
              <select
                className="form-control"
                value={nationality}
                onChange={(e) => setNationality(e.target.value)}
                disabled={!isAdmin && isSigned}
              >
                <option value="">Select nationality</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.country_name}>
                    {c.country_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-12 mb-3">
              <label>Select PDF file *</label>
              <input
                type="file"
                className="form-control"
                accept="application/pdf"
                onChange={handleFileChange}
                disabled={!isAdmin && isSigned}
              />
            </div>
          </div>

          {uploadedFileName && originalFileName && (
            <div className="p-3 bg-light rounded border">
              <div className="text-muted mb-2">Uploaded File:</div>
              <a
                href={`${API_BASE}/uploads/passports/${uploadedFileName}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {originalFileName}
              </a>
              <div className="text-muted">
                Uploaded on: {new Date(createdDate).toLocaleString()}
              </div>
            </div>
          )}
          <div className="mt-4 d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isAdmin && isSigned}
            >
              Save
            </button>
          </div>
        </form>

        <hr className="my-5" />
        <SignatureManager
          taskName="passport"
          taskId={38}
          userId={userId}
          isAdmin={isAdmin}
        />
        <ReviewManager
          taskName="passport"
          taskId={38}
          userId={userId}
          isAdmin={isAdmin}
        />
      </div>
    </>
  );
};

export default Passport;
